/*
 * ot3-pdp-fixes.js
 * Små rettelser til produktsiden. Indlæses fra snippets/ot3-product-actions.liquid.
 */
(() => {
  /*
   * ---------------------------------------------------------------
   * Antalsvælgeren nulstillede sig selv til 1
   *
   * Antalsfeltet ligger inde i <variant-selects>, fordi det skal dele række
   * med farvechippen. Dawns VariantSelects lytter på "change" for hele sit
   * undertræ og publicerer optionValueSelectionChange, så en ændring af
   * antallet blev opfattet som et variantskift: product-info.js hentede
   * sektionen forfra og erstattede feltet med et frisk et med value="1".
   *
   * Den primære vagt er en inline onchange-attribut i
   * product-variant-picker.liquid, fordi den følger med den server-renderede
   * markup ved hvert variantskift. Her sættes den samme vagt på igen som
   * backup, hvis markup'en skulle blive gengivet uden attributten.
   * ---------------------------------------------------------------
   */
  const stopQuantityChange = (event) => {
    if (event.target && event.target.name === 'quantity') {
      event.stopPropagation();
    }
  };

  const bindQuantityGuards = () => {
    document.querySelectorAll('variant-selects .product-form__quantity').forEach((quantityForm) => {
      if (quantityForm.dataset.ot3QtyGuard === 'true') return;
      quantityForm.dataset.ot3QtyGuard = 'true';
      quantityForm.addEventListener('change', stopQuantityChange);
    });
  };

  /*
   * ---------------------------------------------------------------
   * Sammenligningsgruppen flyttes ned efter installationstrinnene
   *
   * Grupperne i sections/ot3-product-details.liquid er hårdkodet i
   * rækkefølgen features → specifikationer → sammenligning → i kassen →
   * installation → produktspørgsmål. Ønsket rækkefølge har sammenligningen
   * mellem installation og produktspørgsmål.
   *
   * Det flyttes her frem for i Liquid, fordi sektionsfilen er 49 KB og kun
   * kan skrives som én samlet tekstblok gennem API'et — en håndafskrift af
   * hele filen for at flytte 129 linjer er en unødig risiko for produktsidens
   * største sektion.
   *
   * insertBefore flytter den faktiske node, ikke bare det visuelle indtryk.
   * Derfor følger både skærmlæsere, tabuleringsrækkefølge og alt andet med —
   * i modsætning til en CSS order-løsning, hvor DOM og visning ville pege
   * hver sin vej. Flytningen sker før sektionen når at komme i syne, da den
   * ligger flere tusinde pixels nede på siden.
   * ---------------------------------------------------------------
   */
  const moveComparisonBeforeFaq = () => {
    const shell = document.querySelector('.ot3-product-details .ot3-shell');
    if (!shell) return;

    const comparison = shell.querySelector(':scope > .ot3-product-details__comparison');
    const faq = shell.querySelector(':scope > .ot3-product-details__group--faq');
    if (!comparison || !faq) return;
    if (comparison.nextElementSibling === faq) return;

    shell.insertBefore(comparison, faq);
  };

  /*
   * ---------------------------------------------------------------
   * SMS og Messenger i del-dialogen
   *
   * Begge bruger app-schemes, der kun åbner noget på tryk-enheder. På
   * desktop ville de være døde knapper, så de afsløres først der.
   * ---------------------------------------------------------------
   */
  const isHandheld =
    window.matchMedia('(hover: none) and (pointer: coarse)').matches ||
    /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  const revealHandheldShareLinks = () => {
    if (!isHandheld) return;
    document.querySelectorAll('[data-ot3-share-app][hidden]').forEach((link) => {
      link.hidden = false;
    });
  };

  /*
   * ---------------------------------------------------------------
   * Kopiér-knappen i del-dialogen
   *
   * ot3-product-uplift.js faldt kun tilbage til execCommand, hvis
   * Clipboard-API'et manglede helt — ikke når kaldet blev afvist. API'et
   * findes i alle moderne browsere, men afvises når dokumentet ikke har
   * fokus, når clipboard-write er nægtet, eller i en iframe uden den
   * tilladelse. Derfor endte man altid i fejlbeskeden.
   *
   * Knappen hedder nu data-ot3-copy-link, så den gamle håndtering ikke
   * matcher, og her er der en rigtig faldstige: Clipboard-API → execCommand
   * → markeret tekst, brugeren selv kan kopiere.
   * ---------------------------------------------------------------
   */
  const selectInputText = (input) => {
    const wasReadOnly = input.readOnly;
    input.readOnly = false;
    input.focus({ preventScroll: true });
    input.select();
    try {
      input.setSelectionRange(0, input.value.length);
    } catch (error) {
      /* Ikke alle inputtyper understøtter setSelectionRange. */
    }
    input.readOnly = wasReadOnly;
  };

  const execCopy = () => {
    try {
      return document.execCommand('copy');
    } catch (error) {
      return false;
    }
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-ot3-copy-link]');
    if (!button) return;

    const input = document.getElementById(button.dataset.ot3CopyLink);
    if (!input) return;

    event.preventDefault();

    const status = button.closest('.ot3-dialog__panel')?.querySelector('[data-ot3-copy-status]');
    const setStatus = (text, ok) => {
      if (!status) return;
      status.textContent = text;
      status.classList.toggle('is-error', !ok);
    };

    const done = () => setStatus('Linket er kopieret.', true);
    const fallback = () => {
      selectInputText(input);
      if (execCopy()) {
        done();
      } else {
        setStatus('Linket er markeret — tryk Ctrl/Cmd + C for at kopiere.', false);
      }
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(input.value).then(done).catch(fallback);
    } else {
      fallback();
    }
  });

  /*
   * ---------------------------------------------------------------
   * Genopbygning efter variantskift
   *
   * "product-info:loaded" udsendes kun fra connectedCallback, altså én gang
   * ved første indlæsning — ved et variantskift udskiftes kun indholdet i
   * <product-info>. Der holdes derfor øje med undertræet.
   *
   * Bindingen sker synkront i observer-callbacket frem for i en
   * requestAnimationFrame: rAF er sat på pause i baggrundsfaner, og så ville
   * vagten først blive sat, når fanen igen var synlig.
   * ---------------------------------------------------------------
   */
  const apply = () => {
    bindQuantityGuards();
    revealHandheldShareLinks();
    moveComparisonBeforeFaq();
    observeProductInfo();
  };

  function observeProductInfo() {
    if (!('MutationObserver' in window)) return;
    document.querySelectorAll('product-info').forEach((productInfo) => {
      if (productInfo.dataset.ot3FixesObserved === 'true') return;
      productInfo.dataset.ot3FixesObserved = 'true';
      new MutationObserver(() => {
        bindQuantityGuards();
        revealHandheldShareLinks();
      }).observe(productInfo, { childList: true, subtree: true });
    });
  }

  apply();

  document.addEventListener('product-info:loaded', apply);
  document.addEventListener('shopify:section:load', apply);
})();
